/**
 * CacheHelper.ts
 *
 * A utility class for client-side caching using browser storage.
 * Provides methods for caching data with TTL, focusing on news data.
 */

export interface CachedItem<T> {
  data: T;
  timestamp: number;
  expiry: number; // Time in milliseconds when the cache expires
}

export interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  storage?: 'local' | 'session'; // Storage type: localStorage or sessionStorage
}

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes in milliseconds
const DEFAULT_STORAGE = 'session';
const NEWS_CACHE_PREFIX = 'ltn_news_';

export class CacheHelper {
  /**
   * Set an item in the cache
   *
   * @param key Cache key
   * @param data Data to store
   * @param options Cache options
   */
  static setItem<T>(key: string, data: T, options?: CacheOptions): void {
    const { ttl = DEFAULT_TTL, storage = DEFAULT_STORAGE } = options || {};
    const timestamp = Date.now();
    const expiry = timestamp + ttl;

    const cachedItem: CachedItem<T> = {
      data,
      timestamp,
      expiry
    };

    const storageObj = storage === 'local' ? localStorage : sessionStorage;
    try {
      storageObj.setItem(key, JSON.stringify(cachedItem));
    } catch (error) {
      console.error('Cache storage error:', error);
      // If storage fails (e.g., quota exceeded), clear some old items
      this.clearOldCacheItems(storageObj);
    }
  }

  /**
   * Get an item from the cache
   *
   * @param key Cache key
   * @param options Cache options
   * @returns The cached data or null if not found or expired
   */
  static getItem<T>(key: string, options?: CacheOptions): T | null {
    const { storage = DEFAULT_STORAGE } = options || {};
    const storageObj = storage === 'local' ? localStorage : sessionStorage;

    const cachedItemJson = storageObj.getItem(key);
    if (!cachedItemJson) return null;

    try {
      const cachedItem = JSON.parse(cachedItemJson) as CachedItem<T>;

      // Check if cache has expired
      if (Date.now() > cachedItem.expiry) {
        storageObj.removeItem(key);
        return null;
      }

      return cachedItem.data;
    } catch (error) {
      console.error('Cache parse error:', error);
      return null;
    }
  }

  /**
   * Remove an item from the cache
   *
   * @param key Cache key
   * @param options Cache options
   */
  static removeItem(key: string, options?: CacheOptions): void {
    const { storage = DEFAULT_STORAGE } = options || {};
    const storageObj = storage === 'local' ? localStorage : sessionStorage;
    storageObj.removeItem(key);
  }

  /**
   * Clear all cached items
   *
   * @param options Cache options
   */
  static clearAll(options?: CacheOptions): void {
    const { storage = DEFAULT_STORAGE } = options || {};
    const storageObj = storage === 'local' ? localStorage : sessionStorage;

    // Only clear items with our prefix
    Object.keys(storageObj).forEach(key => {
      if (key.startsWith(NEWS_CACHE_PREFIX)) {
        storageObj.removeItem(key);
      }
    });
  }

  /**
   * Clear old cache items when storage is full
   *
   * @param storageObj Storage object (localStorage or sessionStorage)
   */
  private static clearOldCacheItems(storageObj: Storage): void {
    const now = Date.now();
    const keysToRemove: string[] = [];

    // Find expired items or items close to expiry
    Object.keys(storageObj).forEach(key => {
      if (key.startsWith(NEWS_CACHE_PREFIX)) {
        try {
          const item = JSON.parse(storageObj.getItem(key) || '{}');
          if (item.expiry && (item.expiry < now || item.expiry < now + 60000)) {
            keysToRemove.push(key);
          }
        } catch (e) {
          // If we can't parse it, remove it
          keysToRemove.push(key);
        }
      }
    });

    // Remove found keys
    keysToRemove.forEach(key => storageObj.removeItem(key));
  }

  // Specific methods for news caching

  /**
   * Cache news data with specific key format
   *
   * @param type News type (latest, hot, popular, etc.)
   * @param params Query parameters
   * @param data News data to cache
   * @param options Cache options
   */
  static cacheNews<T>(type: string, params: Record<string, any>, data: T, options?: CacheOptions): void {
    const cacheKey = this.generateNewsKey(type, params);
    this.setItem(cacheKey, data, options);
  }

  /**
   * Get cached news data
   *
   * @param type News type (latest, hot, popular, etc.)
   * @param params Query parameters
   * @param options Cache options
   * @returns Cached news data or null
   */
  static getCachedNews<T>(type: string, params: Record<string, any>, options?: CacheOptions): T | null {
    const cacheKey = this.generateNewsKey(type, params);
    return this.getItem<T>(cacheKey, options);
  }

  /**
   * Invalidate news cache by type
   *
   * @param type News type to invalidate (or all if not specified)
   * @param options Cache options
   */
  static invalidateNewsCache(type?: string, options?: CacheOptions): void {
    const { storage = DEFAULT_STORAGE } = options || {};
    const storageObj = storage === 'local' ? localStorage : sessionStorage;

    const prefix = type
      ? `${NEWS_CACHE_PREFIX}${type}_`
      : NEWS_CACHE_PREFIX;

    Object.keys(storageObj).forEach(key => {
      if (key.startsWith(prefix)) {
        storageObj.removeItem(key);
      }
    });
  }

  /**
   * Generate a consistent cache key for news
   *
   * @param type News type
   * @param params Query parameters
   * @returns Cache key
   */
  private static generateNewsKey(type: string, params: Record<string, any>): string {
    // Sort params to ensure consistent keys regardless of param order
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((acc, key) => {
        if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
          acc[key] = params[key];
        }
        return acc;
      }, {} as Record<string, any>);

    return `${NEWS_CACHE_PREFIX}${type}_${JSON.stringify(sortedParams)}`;
  }
}

export default CacheHelper;
